import { useState, useEffect, useCallback, useRef } from 'react';
import {
  adminPublicCompanyService,
  AdminCompanyPreviewItem,
} from '../services/adminPublicCompanyService';

export interface UseAdminPublicCompaniesReturn {
  companies: AdminCompanyPreviewItem[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

/**
 * 管理者プレビュー用の企業一覧取得 hook。
 * includeUnpublished トグルが変わるたびに再取得する（キャッシュなし）。
 */
export const useAdminPublicCompanies = (
  includeUnpublished: boolean,
  limit?: number
): UseAdminPublicCompaniesReturn => {
  const [companies, setCompanies] = useState<AdminCompanyPreviewItem[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (mountedRef.current) {
      setLoading(true);
      setError(null);
    }
    try {
      const res = await adminPublicCompanyService.getPreview(
        includeUnpublished,
        limit
      );
      if (mountedRef.current) {
        setCompanies(res.data.companies);
      }
    } catch (err) {
      if (mountedRef.current) {
        setError('企業一覧の取得に失敗しました');
      }
      console.error('管理者向け企業一覧取得エラー:', err);
    } finally {
      if (mountedRef.current) {
        setLoading(false);
      }
    }
  }, [includeUnpublished, limit]);

  useEffect(() => {
    load();
  }, [load]);

  const refetch = useCallback(() => load(), [load]);

  return { companies, loading, error, refetch };
};
